import React, { Component } from 'react';
import SlidePanel from '../shared/SlidePanel';
import { bookingService } from '../../_services/booking.service';
import { hairdresserService } from '../../_services/hairdresser.service';
import { sitesService } from '../../_services/sites.service';
import { loadSites } from '../multiLocation/useSites';
import { formatTime } from './DiaryContext';
import toast from '../shared/Toast';
import DiaryDatePicker from '../Diary/DiaryDatePicker';
import ShiftTimePicker from '../Diary/ShiftTimePicker';
import DurationPicker from '../Diary/DurationPicker';
import StaffPicker from '../Diary/StaffPicker';
import AddClientPanel from '../clients/AddClientPanel';
import { serviceLine } from './serviceLine';
import { computeBookingWindow } from '../shared/computeBookingWindow';
import moment from 'moment';
import './NewBookingPanel.css';
import { RepeatControl, DateCheck } from './repeat/RepeatControl';
import { ruleFor, untilToSend, repeatSaid, endIsValid, bookLabel, needsCheck, checkBody, bookedCount } from './repeat/repeat';
import { bookingSeriesService } from '../../_services/bookingSeries.service';
import { clientSharingService } from '../../_services/clientSharing.service';
import { startPlacement, cancelPlacement, diaryIsOpen, setCheckingDates } from './repeat/placement';
import { rememberRepeat } from './repeat/undo';
import { UndoToast } from './repeat/UndoRepeat';
import { setDraftPreview } from './draftPreview';
import { ONBOARDING_ENABLED } from '../onboarding/flags';
import { openOnboardingLayer } from '../onboarding/ImportJobContext';
import { isIntroActive, introClients, introServiceCategories, addIntroBooking, tellIntro } from '../onboarding/bailey-intro/introStage';

const WALKIN_CLIENT_ID = '64f5c7789a069cda0188fea3';

const PILL_POS_KEY = 'beu-mini-booking-pill-pos';
const PILL_DRAG_THRESHOLD = 5; // px — below this, treat as a click not a drag

function loadPillPosFromStorage() {
  try {
    const raw = localStorage.getItem(PILL_POS_KEY);
    if (!raw) return null;
    const pos = JSON.parse(raw);
    if (typeof pos?.x === 'number' && typeof pos?.y === 'number') return pos;
  } catch (e) { /* ignore */ }
  return null;
}

class NewBookingPanel extends Component {
  constructor(props) {
    super(props);
    this.slidePanelRef = React.createRef();
    this._pillRef = React.createRef();
    this._searchTimeout = null;
    this._pillDrag = null; // { startX, startY, startPosX, startPosY, moved }
    // Bailey's welcome: a practice booking. Example clients and services, and
    // saving puts it on the diary without writing anything to the server.
    this.practice = isIntroActive();
    this._practiceSaved = false;

    this.state = {
      // Pre-filled from diary slot click
      staffId: props.staffId || '',
      bookingDate: props.bookingDate || '',
      startMinutes: props.startMinutes != null ? props.startMinutes : null,

      // Client (pre-fill from rebook if provided)
      clientSearchQuery: '',
      clientSearchResults: [],
      clientSearching: false,
      clientPage: 1,
      clientHasMore: true,
      clientLoadingMore: false,
      selectedClient: props.rebookClient || null,
      // Bailey booking someone new: the add-client form opens filled in.
      showAddClient: !!(props.newClient && !props.rebookClient && !props.initialMinimised && !this.practice),
      minimised: !!props.initialMinimised,

      // Services (pre-fill from rebook if provided)
      services: props.rebookServices || [],
      bookingNote: props.initialBookingNote || '',
      // Repeat bookings: off by default, so a normal booking is unchanged
      repeat: props.initialRepeat && props.initialRepeat.freq
        ? { freq: 'none', end: 'never', endDate: '', endCount: 6, ...props.initialRepeat }
        : { freq: 'none', end: 'never', endDate: '', endCount: 6 },
      clashCheck: null,     // { dates, said } when some dates can't be booked
      clashChoices: {},     // date -> 'keep' | 'skip' | 'move' | 'custom'
      clashTimes: {},       // date -> start minutes they picked for that visit
      availableServices: [],
      servicesLoading: false,

      // UI state
      screen: 'form', // 'form' | 'servicePicker' | 'clientPicker' | 'dateCheck'
      replacingServiceIndex: null,
      serviceSearchQuery: '',
      datePickerOpen: false,

      // "This client belongs to: Salon / <stylist>" — only when salon staff
      // book the salon's client with a self-employed stylist (the server
      // says when: loadSharePrompt). shareWithStylist = their tick.
      sharePrompt: null,
      shareWithStylist: false,

      // Submission
      saving: false,

      // Position of the floating pill. null = use CSS default (bottom-right).
      // Set when the user drags the pill; persisted to localStorage so the
      // chosen spot survives reload across the whole app.
      pillPos: loadPillPosFromStorage(),
    };
  }

  componentDidMount() {
    // The diary shows this booking from the start (in its slot, or under
    // the pointer when it comes back tucked away after a reload)
    this.publishDraft();
    if (this.practice) tellIntro('opened');
    this.loadAvailableServices();
    this.loadSharePrompt();
    // Re-clamp the dragged pill position when the viewport shrinks so it
    // doesn't end up off-screen (especially on rotate / window resize).
    window.addEventListener('resize', this.handlePillViewportResize);
    // The constructor reads pillPos raw from localStorage — a value saved on
    // desktop (e.g. x=800) is off-screen on a mobile viewport. Clamp on
    // first paint, once the pill ref has real dimensions to measure.
    if (this.state.pillPos) this.handlePillViewportResize();
  }

  componentDidUpdate(prevProps, prevState) {
    // A different stylist or client: ask the server again whether to show
    // "This client belongs to".
    if (prevState && (prevState.staffId !== this.state.staffId
      || (prevState.selectedClient && prevState.selectedClient._id) !== (this.state.selectedClient && this.state.selectedClient._id))) {
      this.loadSharePrompt();
    }
    // Anything that moves or resizes the booking redraws it on the diary
    if (prevState && (prevState.staffId !== this.state.staffId || prevState.bookingDate !== this.state.bookingDate
      || prevState.startMinutes !== this.state.startMinutes || prevState.services !== this.state.services
      || prevState.selectedClient !== this.state.selectedClient || prevState.screen !== this.state.screen
      || prevState.minimised !== this.state.minimised)) {
      this.publishDraft();
    }
    // When the host re-points us at a new slot (user clicked another diary
    // slot while the panel was minimised), reflect the new staff/date/start
    // into local state, un-minimise, and reload the staff's services.
    const slotChanged = (
      prevProps.staffId !== this.props.staffId ||
      prevProps.bookingDate !== this.props.bookingDate ||
      prevProps.startMinutes !== this.props.startMinutes
    );
    // The app says the panel is open (a tap on the slot it was already on
    // re-points it without changing anything): open it, so the pill and the
    // diary never disagree about whether a booking is waiting
    if (!slotChanged && prevProps.isMinimised && this.props.isMinimised === false && this.state.minimised) {
      this.setMinimised(false);
    }
    if (slotChanged) {
      this.setMinimised(false);
      this.setState({
        staffId: this.props.staffId,
        bookingDate: this.props.bookingDate,
        startMinutes: this.props.startMinutes,
      }, () => {
        this.loadAvailableServices();
      });
    }
  }

  componentWillUnmount() {
    // Closed mid-check: nothing stays waiting on the diary
    setCheckingDates(false);
    setDraftPreview(null);
    cancelPlacement({ quiet: true });
    clearTimeout(this._flashTimer);
    if (this.practice && !this._practiceSaved) tellIntro('closed');
    if (this._searchTimeout) clearTimeout(this._searchTimeout);
    // Defensive cleanup: if the panel unmounts while the drag-lock style is
    // still in the document (e.g. close happened while minimised), remove it.
    const lock = document.getElementById('dv2-drag-lock');
    if (lock) lock.remove();
    window.removeEventListener('resize', this.handlePillViewportResize);
    // Defensive: drop any in-flight drag listeners if we unmount mid-drag.
    document.removeEventListener('pointermove', this.handlePillPointerMove);
    document.removeEventListener('pointerup', this.handlePillPointerUp);
  }

  // ── Draggable pill ──
  // Clamp a candidate position so the pill stays inside the viewport with
  // an 8px gutter. Falls back to estimated dims if the ref isn't measured.
  clampPillPos = (pos) => {
    const gutter = 8;
    const pillW = this._pillRef.current?.offsetWidth || 220;
    const pillH = this._pillRef.current?.offsetHeight || 56;
    return {
      x: Math.max(gutter, Math.min(pos.x, window.innerWidth - pillW - gutter)),
      y: Math.max(gutter, Math.min(pos.y, window.innerHeight - pillH - gutter)),
    };
  }

  handlePillViewportResize = () => {
    if (!this.state.pillPos) return;
    const clamped = this.clampPillPos(this.state.pillPos);
    if (clamped.x !== this.state.pillPos.x || clamped.y !== this.state.pillPos.y) {
      this.setState({ pillPos: clamped });
    }
  }

  handlePillPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const rect = this._pillRef.current?.getBoundingClientRect();
    this._pillDrag = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: rect ? rect.left : 0,
      startPosY: rect ? rect.top : 0,
      moved: false,
    };
    document.addEventListener('pointermove', this.handlePillPointerMove);
    document.addEventListener('pointerup', this.handlePillPointerUp);
    // Stops default browser gestures (text selection, page scroll on touch)
    // while we own the drag.
    e.preventDefault();
  }

  handlePillPointerMove = (e) => {
    if (!this._pillDrag) return;
    const dx = e.clientX - this._pillDrag.startX;
    const dy = e.clientY - this._pillDrag.startY;
    if (!this._pillDrag.moved) {
      if (Math.abs(dx) < PILL_DRAG_THRESHOLD && Math.abs(dy) < PILL_DRAG_THRESHOLD) return;
      this._pillDrag.moved = true;
    }
    this.setState({
      pillPos: this.clampPillPos({
        x: this._pillDrag.startPosX + dx,
        y: this._pillDrag.startPosY + dy,
      }),
    });
  }

  handlePillPointerUp = () => {
    document.removeEventListener('pointermove', this.handlePillPointerMove);
    document.removeEventListener('pointerup', this.handlePillPointerUp);
    const drag = this._pillDrag;
    this._pillDrag = null;
    if (!drag) return;
    if (drag.moved) {
      // Persist the new position so it survives reload + lives on every page.
      try { localStorage.setItem(PILL_POS_KEY, JSON.stringify(this.state.pillPos)); } catch (e) { /* ignore */ }
    } else {
      // Swallow the synthetic click that browsers fire after a touch
      // pointerup. Without this the puck unmounts on minimised=false, the
      // synthetic click lands on the SlidePanel backdrop underneath, and
      // SlidePanel's onClose immediately re-minimises the panel — looking
      // like an auto-close right after the open animation.
      const swallow = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        window.removeEventListener('click', swallow, true);
      };
      window.addEventListener('click', swallow, true);
      // Safety: if no synthetic click fires (mouse mode), drop the listener.
      setTimeout(() => window.removeEventListener('click', swallow, true), 350);
      this.setMinimised(false);
    }
  }

  // Snapshot the form fields that need to survive route changes / reload.
  // Shape matches the `draft` field of the minimisedBooking redux slice.
  getDraftSnapshot = () => {
    const { staffId, bookingDate, startMinutes, selectedClient, services, bookingNote } = this.state;
    // The repeat survives minimising too
    return { staffId, bookingDate, startMinutes, selectedClient, services, bookingNote, repeat: this.state.repeat };
  }

  // What the diary shows of this booking: tucked away, a preview under the
  // pointer; open, the booking drawn in its slot behind the panel
  publishDraft = (minimised = this.state.minimised) => {
    if (this.practice || this.state.screen === 'dateCheck') { setDraftPreview(null); return; }
    const duration = Math.max(15, this.getTotalDuration() || 0);
    const label = (this.state.selectedClient && this.state.selectedClient.name) || 'New booking';
    const { staffId, bookingDate, startMinutes } = this.state;
    const date = bookingDate ? moment(bookingDate).format('YYYY-MM-DD') : '';
    setDraftPreview({
      duration,
      label,
      pinned: !minimised && staffId && date && startMinutes != null ? { staffId: String(staffId), date, minutes: startMinutes } : null,
      hover: !!minimised,
    });
  }

  setMinimised = (val) => {
    // A practice booking is never kept as a draft: minimising closes it, so
    // nothing from the tour can linger in the pill after it ends.
    if (this.practice && val) {
      if (this.props.onClose) this.props.onClose();
      return;
    }
    // Inject/remove a global style to block all event drag interactions
    if (val) {
      if (!document.getElementById('dv2-drag-lock')) {
        const style = document.createElement('style');
        style.id = 'dv2-drag-lock';
        style.textContent = '.dv2-event-block { pointer-events: none !important; cursor: default !important; } .dv2-resize-handle { display: none !important; } .dv2-ghost-block { display: none !important; }';
        document.head.appendChild(style);
      }
    } else {
      const existing = document.getElementById('dv2-drag-lock');
      if (existing) existing.remove();
    }
    if (!val) cancelPlacement({ quiet: true });
    // Tucked away: a preview where they hover; open: drawn in its slot
    this.publishDraft(val);
    this.setState({ minimised: val }, () => {
      // When minimising, hand the snapshot up so the host can persist it.
      // When restoring, no snapshot is needed.
      if (this.props.onMinimiseChange) {
        this.props.onMinimiseChange(val, val ? this.getDraftSnapshot() : undefined);
      }
    });
  }

  // ── Service Loading ──
  loadAvailableServices = () => {
    if (this.practice) {
      this.setState({ availableServices: introServiceCategories(), servicesLoading: false });
      return;
    }
    const hairdresserId = this.state.staffId || localStorage.getItem('userId');
    if (!hairdresserId) return;
    this.setState({ servicesLoading: true });
    hairdresserService.getServicesGroupedV2({ hairdresserId })
      .then(response => {
        const categories = response?.data?.data?.hairdresserSelectedServices || [];
        this.setState({ availableServices: Array.isArray(categories) ? categories : [], servicesLoading: false });
      })
      .catch(() => { this.setState({ servicesLoading: false }); });
  }

  // ── Client Search ──
  handleClientSearch = (query) => {
    if (this.practice) {
      this.setState({ clientSearchQuery: query, clientSearchResults: introClients(query), clientSearching: false, clientHasMore: false });
      return;
    }
    this.setState({ clientSearchQuery: query, clientPage: 1, clientHasMore: true });
    if (this._searchTimeout) clearTimeout(this._searchTimeout);

    const doFetch = (searchKey) => {
      this.setState({ clientSearching: true });
      hairdresserService.getClientsPaginated({ searchKey, page: 1, limit: 20, sortField: 'name', sortOrder: 'asc' })
        .then(response => {
          const data = response?.data?.responseData || response?.data?.data || {};
          const clients = data.clients || data.clientList || [];
          const arr = Array.isArray(clients) ? clients : [];
          this.setState({ clientSearchResults: arr, clientSearching: false, clientPage: 1, clientHasMore: arr.length >= 20 });
          this.searchOtherSites(searchKey, arr);
        })
        .catch(() => { this.setState({ clientSearchResults: [], clientSearching: false, clientHasMore: false }); });
    };

    if (!query.trim()) {
      doFetch('');
    } else {
      this._searchTimeout = setTimeout(() => doFetch(query.trim()), 300);
    }
  }

  // Multi-location: a client of one of the owner's other locations is found
  // here too (Shared → Clients: "Also visits" / "Shared"). Rows carry
  // `_otherSite` so the picker can say where they're known from; picking one
  // books them here by their user id, which creates their record at this
  // location the same way an online booking would.
  searchOtherSites = (searchKey, localRows) => {
    const q = String(searchKey || '').trim();
    if (q.length < 2) return;
    loadSites(false).then((d) => {
      if (!d || !d.sites || d.sites.length < 2) return null;
      return sitesService.clientSearch(q, 10);
    }).then((r) => {
      if (!r || !Array.isArray(r.results) || !r.results.length) return;
      if (this.state.clientSearchQuery.trim() !== q) return;
      const seen = new Set((localRows || []).map((a) => String(a.userId || a._id)));
      const extra = r.results
        .filter((x) => x.userId && !seen.has(String(x.userId)))
        .map((x) => ({ _id: x.userId, userId: x.userId, name: x.name, email: x.email, phoneNumber: x.phoneNumber, _otherSite: x.salonName }));
      if (!extra.length) return;
      this.setState((prev) => ({ clientSearchResults: [...prev.clientSearchResults, ...extra] }));
    }).catch(() => { /* other-site search is best-effort */ });
  }

  handleClientScroll = (e) => {
    const { clientPage, clientHasMore, clientLoadingMore, clientSearchQuery } = this.state;
    if (this.practice || !clientHasMore || clientLoadingMore) return;
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      const nextPage = clientPage + 1;
      this.setState({ clientLoadingMore: true });
      hairdresserService.getClientsPaginated({ searchKey: clientSearchQuery.trim(), page: nextPage, limit: 20, sortField: 'name', sortOrder: 'asc' })
        .then(response => {
          const data = response?.data?.responseData || response?.data?.data || {};
          const clients = data.clients || data.clientList || [];
          const arr = Array.isArray(clients) ? clients : [];
          this.setState(prev => ({
            clientSearchResults: [...prev.clientSearchResults, ...arr],
            clientPage: nextPage,
            clientHasMore: arr.length >= 20,
            clientLoadingMore: false,
          }));
        })
        .catch(() => { this.setState({ clientLoadingMore: false, clientHasMore: false }); });
    }
  }

  // Booking the salon's client with a self-employed stylist: whether the
  // client also belongs to the stylist. The server decides when to ask (salon
  // staff, a self-employed stylist, the salon's own client not already
  // theirs) and what the salon's setting ticks by default.
  loadSharePrompt = () => {
    const { staffId, selectedClient } = this.state;
    const clientId = selectedClient && selectedClient._id;
    const key = `${staffId}|${clientId}`;
    this._shareKey = key;
    if (this.practice || !staffId || !clientId || clientId === WALKIN_CLIENT_ID) {
      if (this.state.sharePrompt) this.setState({ sharePrompt: null, shareWithStylist: false });
      return;
    }
    clientSharingService.sharePrompt(staffId, clientId)
      .then((p) => {
        if (this._shareKey !== key) return;
        this.setState(p && p.ask
          ? { sharePrompt: p, shareWithStylist: !!p.defaultShare }
          : { sharePrompt: null, shareWithStylist: false });
      })
      .catch(() => { if (this._shareKey === key) this.setState({ sharePrompt: null, shareWithStylist: false }); });
  }

  renderBelongsTo() {
    const { sharePrompt, shareWithStylist } = this.state;
    if (!sharePrompt) return null;
    const name = sharePrompt.stylistName;
    // The business's own name, never "Salon" (barbers and more).
    const biz = sharePrompt.businessName || 'The business';
    return (
      <div className="nbp-belongs">
        <span className="nbp-belongs-label">This client belongs to</span>
        <div className="nbp-belongs-options">
          <label className="nbp-belongs-option nbp-belongs-option--fixed">
            <input type="checkbox" checked readOnly disabled />
            <span>{biz}</span>
          </label>
          <label className="nbp-belongs-option">
            <input
              type="checkbox"
              checked={shareWithStylist}
              onChange={(e) => this.setState({ shareWithStylist: e.target.checked })}
            />
            <span>{name}</span>
          </label>
        </div>
        <span className="nbp-belongs-hint">
          {shareWithStylist
            ? `They'll be in ${name}'s clients as well as ${biz}'s.`
            : `They'll stay in ${biz}'s clients only.`}
        </span>
      </div>
    );
  }

  handleSelectClient = (client) => {
    this.setState({ selectedClient: client, clientSearchQuery: '', clientSearchResults: [] });
    if (this.practice) tellIntro('client', { clientName: client.name });
  }

  handleWalkIn = () => {
    this.setState({
      selectedClient: { _id: WALKIN_CLIENT_ID, name: 'Walk-in' },
      clientSearchQuery: '',
      clientSearchResults: [],
    });
    if (this.practice) tellIntro('client', { clientName: 'Walk-in' });
  }

  handleClearClient = () => {
    this.setState({ selectedClient: null });
  }

  // ── Service Picker ──
  handleOpenServicePicker = (index) => {
    this.setState({ screen: 'servicePicker', replacingServiceIndex: index, serviceSearchQuery: '' });
  }

  handleCloseServicePicker = () => {
    this.setState({ screen: 'form', serviceSearchQuery: '', replacingServiceIndex: null });
  }

  handleSelectService = (service) => {
    const { replacingServiceIndex, services, staffId } = this.state;
    const newService = serviceLine(service, staffId);
    // eslint-disable-next-line no-console
    console.log('[NewBookingPanel] picked service KEYS:', Object.keys(service).sort().join(', '));
    // eslint-disable-next-line no-console
    console.log('[NewBookingPanel] picked service VALUES:', {
      serviceName: service.serviceName,
      'service.setupTime': service.setupTime,
      'service.cleanupTime': service.cleanupTime,
      'service.processingTime': service.processingTime,
      resolvedSetup: newService.setupTime, resolvedCleanup: newService.cleanupTime, resolvedProcessing: newService.processingTime,
      raw: service,
    });

    let updated;
    if (replacingServiceIndex !== null && replacingServiceIndex < services.length) {
      updated = services.map((s, i) => i === replacingServiceIndex ? newService : s);
    } else {
      updated = [...services, newService];
    }

    this.setState({ services: updated, screen: 'form', serviceSearchQuery: '', replacingServiceIndex: null });
    if (this.practice) tellIntro('service', { serviceName: service.serviceName });
  }

  handleRemoveService = (index) => {
    this.setState(prev => ({ services: prev.services.filter((_, i) => i !== index) }));
  }

  handleMoveService = (index, direction) => {
    this.setState(prev => {
      const arr = [...prev.services];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= arr.length) return null;

      // Swap positions.
      [arr[index], arr[targetIndex]] = [arr[targetIndex], arr[index]];

      // On reorder, re-derive each service's setup/cleanup from its OWN
      // definition (s.service.setupTime / s.service.cleanupTime). Falls back
      // to the entry's existing value if the nested definition isn't on
      // hand (services added via picker store only entry-level values).
      // Mid-chain values always zero — multi-service rule.
      //
      // Reasoning: a structural reorder is the user saying "use this new
      // layout's natural buffers". Trusting each service's definition is
      // the salon-friendly default. If a user wants a different value
      // post-reorder, they can override via the picker.
      const newArr = arr.map((s, i) => {
        const defSetup = parseInt(s.service?.setupTime ?? s.setupTime, 10) || 0;
        const defCleanup = parseInt(s.service?.cleanupTime ?? s.cleanupTime, 10) || 0;
        return {
          ...s,
          setupTime: i === 0 ? defSetup : 0,
          cleanupTime: i === arr.length - 1 ? defCleanup : 0,
        };
      });

      return { services: newArr };
    });
  }

  handleServiceDurationChange = (index, value) => {
    const mins = Math.max(5, parseInt(value) || 5);
    this.setState(prev => ({
      services: prev.services.map((s, i) => i === index ? { ...s, serviceTime: mins } : s),
    }));
  }

  handleProcessingTimeChange = (index, value) => {
    const mins = Math.max(0, parseInt(value) || 0);
    this.setState(prev => ({
      services: prev.services.map((s, i) => i === index ? { ...s, processingTime: mins } : s),
    }));
  }

  handleFinishingTimeChange = (index, value) => {
    const mins = Math.max(0, parseInt(value) || 0);
    this.setState(prev => ({
      services: prev.services.map((s, i) => i === index ? { ...s, finishingTime: mins } : s),
    }));
  }

  // Setup buffer lives on the first service. Cleanup lives on the last.
  // Mid-chain values are zeroed in the save payload so the multi-service rule
  // is enforced even if a stylist re-orders services.
  handleSetupTimeChange = (value) => {
    const mins = Math.max(0, parseInt(value) || 0);
    this.setState(prev => {
      if (prev.services.length === 0) return null;
      return {
        services: prev.services.map((s, i) => i === 0 ? { ...s, setupTime: mins } : s),
      };
    });
  }

  handleCleanupTimeChange = (value) => {
    const mins = Math.max(0, parseInt(value) || 0);
    this.setState(prev => {
      if (prev.services.length === 0) return null;
      const lastIdx = prev.services.length - 1;
      return {
        services: prev.services.map((s, i) => i === lastIdx ? { ...s, cleanupTime: mins } : s),
      };
    });
  }

  // ── Calculations ──
  // The total slot duration includes the first service's setup, every
  // service's running time, processing time between services, and the last
  // service's cleanup. Computed via the shared helper so client-side and
  // diary-side stay in lock-step.
  getTotalDuration() {
    const window = computeBookingWindow(this.state.services);
    // eslint-disable-next-line no-console
    if (this.state.services.length > 0) {
      console.log('[NewBookingPanel] computeBookingWindow', window, 'from services', this.state.services);
    }
    return window.totalSlotMinutes;
  }

  getServiceStartMinutes() {
    // The booking slot starts at the first setup buffer, but the actual
    // first service begins after that buffer.
    if (this.state.startMinutes == null) return 0;
    const { setupMinutes } = computeBookingWindow(this.state.services);
    return this.state.startMinutes + setupMinutes;
  }

  getEndMinutes() {
    if (this.state.startMinutes == null) return 0;
    return this.state.startMinutes + this.getTotalDuration();
  }

  getTotalAmount() {
    return this.state.services.reduce((sum, s) => sum + (parseFloat(s.servicePrice) || 0), 0);
  }

  // ── Save ──
  handleSave = (andCheckout = false) => {
    const { staffId, bookingDate, startMinutes, selectedClient, services, bookingNote } = this.state;

    if (!bookingDate) {
      toast.error('Please select a date');
      return;
    }
    if (startMinutes == null) {
      toast.error('Please select a start time');
      return;
    }
    if (services.length === 0) {
      toast.error('Please add at least one service');
      return;
    }

    // Default to walk-in if no client selected
    const client = selectedClient || { _id: WALKIN_CLIENT_ID, name: 'Walk-in' };

    const endMinutes = this.getEndMinutes();

    // Practice booking: straight onto the diary, nothing sent anywhere.
    if (this.practice) {
      const booking = {
        staffId,
        bookingDate: moment(bookingDate).format('YYYY-MM-DD'),
        startMinutes,
        endMinutes,
        clientId: client._id,
        clientName: client.name,
        services: services.map((s) => ({ id: s.serviceId, name: s.serviceName, price: parseFloat(s.servicePrice || 0), time: s.serviceTime, colour: s.diaryColour })),
      };
      this._practiceSaved = true;
      addIntroBooking(booking);
      tellIntro('saved', booking);
      if (this.props.onClose) this.props.onClose();
      return;
    }
    const params = {
      servicesToBook: services.map((s, i) => ({
        ...s,
        id: s.service?.serviceId || s.service?._id || s.serviceId,
        _id: s.service?.serviceId || s.service?._id || s.serviceId,
        serviceId: s.service?.serviceId || s.service?._id || s.serviceId,
        price: parseFloat(s.servicePrice || 0),
        // Honour the multi-service rule on the wire too: only the first
        // service's setupTime and the last service's cleanupTime are sent
        // through, mid-chain ones are zeroed so the backend can't double-
        // count them.
        setupTime: i === 0 ? (parseInt(s.setupTime, 10) || 0) : 0,
        cleanupTime: i === services.length - 1 ? (parseInt(s.cleanupTime, 10) || 0) : 0,
      })),
      bookingSlot: [startMinutes, endMinutes],
      hairdresserId: staffId,
      bookingDate: moment(bookingDate).format('YYYY-MM-DD'),
      clientId: client._id,
      salonId: localStorage.getItem('salonId'),
      totalAmount: this.getTotalAmount(),
      invoiceNumber: localStorage.getItem('invoiceNumber'),
      bookingNote: bookingNote || '',
      transaction: [],
    };
    // Only sent when the panel asked; otherwise the salon's setting applies.
    if (this.state.sharePrompt) params.shareClientWithStylist = !!this.state.shareWithStylist;

    // Repeating: check the dates first; save straight away when they're all
    // bookable, otherwise show just the ones that aren't (saveSeries)
    if (ruleFor(this.state.repeat.freq)) {
      this.startSeries(params, andCheckout);
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[NewBookingPanel] createBookingV2 params', JSON.parse(JSON.stringify(params)));
    this.setState({ saving: true });
    bookingService.createBookingV2(params)
      .then(response => {
        this.setState({ saving: false });
        if (response.data?.statusCode !== 1) {
          toast.error(response.data?.error?.message || response.data?.responseData?.message || 'Error creating booking');
          return;
        }
        toast.success('Booking created');
        const bookingId = response.data?.responseData?.result?._id;
        // Save booking note separately if provided
        if (bookingId && bookingNote && bookingNote.trim()) {
          bookingService.updateBookingNotes(bookingId, bookingNote.trim()).catch(() => {});
        }
        if (andCheckout && bookingId && this.props.history) {
          this.props.history.push({ pathname: '/checkout/' + bookingId, state: { pageLoadingStatus: 1 } });
        }
        // The id lets the diary find the new block and bring it in.
        if (this.props.onCreated) this.props.onCreated(bookingId);
      })
      .catch(() => {
        this.setState({ saving: false });
        toast.error('Something went wrong');
      });
  }

  // ── Repeat bookings ──
  seriesParams(params) {
    const { repeat } = this.state;
    return {
      ...params,
      bookingNotes: params.bookingNote || '',
      rule: ruleFor(repeat.freq),
      until: untilToSend(repeat, this.state.bookingDate),
    };
  }

  startSeries = (params, andCheckout) => {
    const { repeat, bookingDate } = this.state;
    if (!endIsValid({ ...repeat, firstDate: bookingDate })) {
      toast.error(repeat.end === 'date' ? 'Choose a last date after the first visit' : 'Choose 2 visits or more');
      return;
    }
    const body = this.seriesParams(params);
    this._seriesBody = body;
    this._seriesCheckout = andCheckout;
    this.setState({ saving: true });
    bookingSeriesService.check(body)
      .then((res) => {
        // Any date that overlaps another booking or can't be booked: show
        // every date first. All free: book straight away.
        if (needsCheck(res)) {
          setCheckingDates(true);
          this.setState({ saving: false, clashCheck: res, clashChoices: {}, clashTimes: {}, screen: 'dateCheck' });
          return;
        }
        this.saveSeries();
      })
      .catch((e) => {
        this.setState({ saving: false });
        toast.error(e.message || 'Something went wrong');
      });
  }

  saveSeries = () => {
    const body = this._seriesBody;
    if (!body) return;
    const { clashCheck, clashChoices } = this.state;
    const { skip, moves } = checkBody((clashCheck && clashCheck.dates) || [], clashChoices, this.state.clashTimes, this.getTotalDuration());
    // Overlaps they chose to book anyway, for the toast
    const kept = ((clashCheck && clashCheck.dates) || []).filter(d => d.status === 'overlap' && !skip.includes(d.date) && !moves[d.date]).length;
    this.setState({ saving: true });
    bookingSeriesService.create({ ...body, skip, moves })
      .then((res) => {
        setCheckingDates(false);
        this.setState({ saving: false, clashCheck: null, screen: 'form' });
        const n = (res.created || []).length;
        const said = repeatSaid({ ...this.state.repeat, firstDate: this.state.bookingDate });
        const extra = [
          (res.skipped || []).length ? `${res.skipped.length} skipped` : null,
          (res.failed || []).length ? `${res.failed.length} couldn’t be booked` : null,
          kept ? `${kept} overlap${kept === 1 ? 's' : ''} another booking` : null,
        ].filter(Boolean).join(', ');
        const words = `Booked ${n} visit${n === 1 ? '' : 's'}. ${said}${extra ? ` (${extra})` : ''}`;
        // Booked by mistake: Undo, here for a while and on the booking for
        // 10 minutes (repeat/undo.js)
        if (res.seriesId) {
          rememberRepeat(res.seriesId);
          toast.success(<UndoToast text={words} seriesId={res.seriesId} />, 12000);
        } else {
          toast.success(words);
        }
        const first = (res.created || [])[0];
        const bookingId = first && first.bookingId;
        if (this._seriesCheckout && bookingId && this.props.history) {
          this.props.history.push({ pathname: '/checkout/' + bookingId, state: { pageLoadingStatus: 1 } });
        }
        if (this.props.onCreated) this.props.onCreated(bookingId);
      })
      .catch((e) => {
        this.setState({ saving: false });
        toast.error(e.message || 'Something went wrong');
      });
  }

  // ── Renders ──
  // Check the dates: every visit of the repeat before anything is booked
  // The clashes still to look at, in date order
  problemDates = () => (((this.state.clashCheck && this.state.clashCheck.dates) || []).filter(d => d.status !== 'ok'));

  // Back on Check the dates after the diary: the row they just did, in view
  // and flashed, so they can carry on down the list
  backToList = (date) => {
    this.setState({ screen: 'dateCheck', lastPlaced: date });
    this.setMinimised(false);
    setTimeout(() => {
      const row = document.querySelector(`.rpt-visit[data-date="${date}"]`);
      if (row && row.scrollIntoView) row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 350);
    clearTimeout(this._flashTimer);
    this._flashTimer = setTimeout(() => this.setState({ lastPlaced: null }), 2200);
  }

  // A visit on the diary, one at a time: the panel tucks away to its pill,
  // the diary goes to that day and the visit sits there as a block they
  // drag (or tap) to a new time, seeing the clashes. "Use 11:30" comes back
  // to the list; "Use 11:30, next" goes on to the next clash. Only that
  // visit moves. See it in the diary is the same, starting where it is.
  placeOnDiary = (d) => {
    const { staffId, startMinutes, selectedClient, clashTimes } = this.state;
    const list = this.problemDates();
    const i = list.findIndex(x => x.date === d.date);
    const next = i >= 0 ? list[i + 1] : null;
    try {
      window.dispatchEvent(new CustomEvent('beu:diary-go-to-date', { detail: { date: d.date, staffId } }));
    } catch (e) { /* ignore */ }
    startPlacement({
      date: d.date,
      staffId,
      minutes: clashTimes[d.date] != null ? clashTimes[d.date] : startMinutes,
      original: startMinutes,
      duration: this.getTotalDuration(),
      label: selectedClient && selectedClient.name ? selectedClient.name : '',
      step: { index: i + 1, total: list.length, hasNext: !!next },
    }, {
      onDone: (minutes, t, { next: goOn } = {}) => {
        // The usual time: nothing to move (an overlap stays "Book anyway")
        if (minutes !== startMinutes) {
          this.setState(st => ({ clashTimes: { ...st.clashTimes, [d.date]: minutes }, clashChoices: { ...st.clashChoices, [d.date]: 'custom' } }));
        }
        if (goOn && next) { setTimeout(() => this.placeOnDiary(next), 0); return; }
        this.backToList(d.date);
      },
      onCancel: () => this.backToList(d.date),
    });
    this.setMinimised(true);
  }

  viewInDiary = d => this.placeOnDiary(d)

  changeTimeOnDiary = d => this.placeOnDiary(d)

  renderDateCheck() {
    const { clashCheck, clashChoices, clashTimes, saving, startMinutes } = this.state;
    const dates = (clashCheck && clashCheck.dates) || [];
    const n = bookedCount(dates, clashChoices, clashTimes);
    const back = () => { setCheckingDates(false); this.setState({ screen: 'form', clashCheck: null, clashChoices: {}, clashTimes: {} }); };
    return (
      <>
        <div className="bdp-header">
          <div className="bdp-header-left">
            <button className="bdp-back-btn" onClick={back} aria-label="Back to the booking">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">Check the dates</h3>
            </div>
          </div>
          <button className="bdp-close-btn" onClick={this.props.onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="bdp-body">
          <div className="rpt-check-wrap">
            <DateCheck
              dates={dates}
              choices={clashChoices}
              times={clashTimes}
              startMinutes={startMinutes}
              onTime={(date, minutes) => this.setState(st => ({ clashTimes: { ...st.clashTimes, [date]: minutes }, clashChoices: { ...st.clashChoices, [date]: 'custom' } }))}
              onView={this.viewInDiary}
              onChangeTime={diaryIsOpen() ? this.changeTimeOnDiary : null}
              timeLabel={startMinutes != null ? formatTime(startMinutes) : ''}
              flashDate={this.state.lastPlaced}
              onChoose={(date, how) => this.setState(st => ({ clashChoices: { ...st.clashChoices, [date]: how } }))}
              onChooseAll={(status, how) => this.setState((st) => {
                const next = { ...st.clashChoices };
                // No time to move to: an overlap is booked anyway, a blocked date skipped
                dates.filter(d => d.status === status).forEach((d) => { next[d.date] = how === 'move' && !d.suggestion ? (d.status === 'overlap' ? 'keep' : 'skip') : how; });
                return { clashChoices: next };
              })}
            />
          </div>
        </div>
        <div className="bdp-footer">
          <button className="bdp-btn bdp-btn--secondary" onClick={back} disabled={saving}>Back</button>
          <button className="bdp-btn bdp-btn--primary" onClick={this.saveSeries} disabled={saving || n === 0}>
            {saving ? 'Booking...' : n === 0 ? 'Nothing to book' : `Book ${n} visit${n === 1 ? '' : 's'}`}
          </button>
        </div>
      </>
    );
  }

  renderClientPicker() {
    const { clientSearchQuery, clientSearchResults, clientSearching } = this.state;

    return (
      <>
        <div className="bdp-header">
          <div className="bdp-header-left">
            <button className="bdp-back-btn" onClick={() => this.setState({ screen: 'form', clientSearchQuery: '', clientSearchResults: [] })}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">Select Client</h3>
            </div>
          </div>
          <button className="bdp-close-btn" onClick={this.props.onClose}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="bdp-picker-search">
          <svg className="bdp-picker-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="bdp-picker-search-input"
            placeholder="Search by name, email, or phone..."
            value={clientSearchQuery}
            onChange={(e) => this.handleClientSearch(e.target.value)}
            autoFocus={window.innerWidth >= 768}
          />
        </div>
        <div className="bdp-picker-list bdp-picker-list--fullscreen" onScroll={this.handleClientScroll}>
          {/* Walk-in option always at top */}
          <button type="button" className="nbp-client-picker-item nbp-client-picker-item--walkin" onClick={() => { this.handleWalkIn(); this.setState({ screen: 'form' }); }}>
            <div className="nbp-client-picker-avatar nbp-client-picker-avatar--walkin">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3C6.9 3 6 3.9 6 5C6 6.1 6.9 7 8 7C9.1 7 10 6.1 10 5C10 3.9 9.1 3 8 3Z" fill="currentColor"/>
                <path d="M8 8.5C5.79 8.5 4 9.79 4 11.5V13H12V11.5C12 9.79 10.21 8.5 8 8.5Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="nbp-client-picker-info">
              <span className="nbp-client-picker-name">Walk-in Client</span>
              <span className="nbp-client-picker-sub">No client details required</span>
            </div>
          </button>

          {/* Add new client */}
          <button type="button" className="nbp-client-picker-item nbp-client-picker-item--add" onClick={() => {
            if (this.practice) { toast.info('For the tour, pick one of the example clients.'); return; }
            this.setState({ showAddClient: true });
          }}>
            <div className="nbp-client-picker-avatar nbp-client-picker-avatar--add">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="nbp-client-picker-info">
              <span className="nbp-client-picker-name">New Client</span>
              <span className="nbp-client-picker-sub">Create a new client profile</span>
            </div>
          </button>

          {clientSearching && (
            <div className="bdp-picker-loading">Searching...</div>
          )}

          {!clientSearching && clientSearchQuery.trim() && clientSearchResults.length === 0 && (
            <div className="bdp-picker-loading">No clients found</div>
          )}

          {clientSearchResults.map((c, i) => (
            <button
              key={i}
              type="button"
              className={`nbp-client-picker-item${c._example ? ' nbp-client-picker-item--example' : ''}`}
              onClick={() => { this.handleSelectClient(c); this.setState({ screen: 'form' }); }}
            >
              <div className="nbp-client-picker-avatar">
                {(c.name || '?')[0]}
              </div>
              <div className="nbp-client-picker-info">
                <span className="nbp-client-picker-name">{c.name}</span>
                {c.email && !c.email.includes('@beuapp.com') && (
                  <span className="nbp-client-picker-sub">{c.email}</span>
                )}
                {c.phoneNumber && (
                  <span className="nbp-client-picker-sub">{c.phoneNumber}</span>
                )}
                {c._otherSite && (
                  <span className="nbp-client-picker-sub">Also visits · {c._otherSite}</span>
                )}
                {c._example && <span className="nbp-client-picker-sub">Example client</span>}
              </div>
            </button>
          ))}
          {this.state.clientLoadingMore && (
            <div className="bdp-picker-loading">Loading more...</div>
          )}
        </div>
      </>
    );
  }

  renderServicePicker() {
    const { services, serviceSearchQuery, availableServices, servicesLoading, replacingServiceIndex } = this.state;
    const currency = this.props.currencySymbol || '£';
    const pickerTitle = replacingServiceIndex !== null ? 'Change Service' : 'Add Service';

    return (
      <>
        <div className="bdp-header">
          <div className="bdp-header-left">
            <button className="bdp-back-btn" onClick={this.handleCloseServicePicker}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">{pickerTitle}</h3>
            </div>
          </div>
          <button className="bdp-close-btn" onClick={this.props.onClose}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        <div className="bdp-picker-search">
          <svg className="bdp-picker-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="bdp-picker-search-input"
            placeholder="Search services..."
            value={serviceSearchQuery}
            onChange={(e) => this.setState({ serviceSearchQuery: e.target.value })}
            autoFocus={window.innerWidth >= 768}
          />
        </div>
        <div className="bdp-picker-list bdp-picker-list--fullscreen">
          {servicesLoading ? (
            <div className="bdp-picker-loading">Loading services...</div>
          ) : (
            (() => {
              const query = serviceSearchQuery.toLowerCase().trim();
              let hasResults = false;
              const content = availableServices.map((cat, ci) => {
                const catServices = cat.hairdresserService || cat.services || [];
                const filtered = query
                  ? catServices.filter(s => (s.serviceName || '').toLowerCase().includes(query))
                  : catServices;
                if (filtered.length === 0) return null;
                hasResults = true;
                return (
                  <div key={ci} className="bdp-picker-category">
                    {!query && <div className="bdp-picker-category-label">{cat.categoryName || 'Services'}</div>}
                    {filtered.map((svc, si) => {
                      const svcId = svc._id || svc.serviceId;
                      const alreadyAdded = services.some(es => (es.service?.serviceId || es.serviceId) === svcId);
                      return (
                        <button
                          key={si}
                          type="button"
                          className={`bdp-picker-item ${alreadyAdded ? 'bdp-picker-item--added' : ''}`}
                          onClick={() => !alreadyAdded && this.handleSelectService(svc)}
                          disabled={alreadyAdded}
                        >
                          <span className="bdp-picker-item-color" style={{ background: svc.diaryColour || '#D6D5EC' }} />
                          <div className="bdp-picker-item-info">
                            <span className="bdp-picker-item-name">{svc.serviceName}</span>
                            <span className="bdp-picker-item-meta">{svc.serviceTime} min</span>
                          </div>
                          <span className="bdp-picker-item-price">{currency}{parseFloat(svc.servicePrice || 0).toFixed(2)}</span>
                          {alreadyAdded && (
                            <svg className="bdp-picker-item-check" width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#007EA0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              });
              if (!hasResults) {
                // Onboarding safety net: a brand new salon has nothing to book yet.
                if (!query && !(availableServices || []).length && ONBOARDING_ENABLED) {
                  return (
                    <div className="bdp-picker-loading">
                      <div>No services yet</div>
                      <button
                        type="button"
                        className="nbp-starter-btn"
                        onClick={() => { if (this.props.onClose) this.props.onClose(); openOnboardingLayer({ mode: 'setup', screen: 'menu' }); }}
                      >
                        Add a starter menu
                      </button>
                    </div>
                  );
                }
                return <div className="bdp-picker-loading">{query ? 'No services found' : 'No services available'}</div>;
              }
              return content;
            })()
          )}
        </div>
      </>
    );
  }

  renderForm() {
    const { staffId, bookingDate, startMinutes, selectedClient, services, bookingNote, saving, datePickerOpen } = this.state;
    const { staffList, currencySymbol: propCurrency } = this.props;
    const currency = propCurrency || '£';
    const totalAmount = this.getTotalAmount();
    const totalDuration = this.getTotalDuration();
    const endMinutes = this.getEndMinutes();

    return (
      <>
        {/* Header */}
        <div className="bdp-header">
          <div className="bdp-header-left">
            <div className="nbp-header-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <rect x="2" y="3" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M2 7H16" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M6 1V4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                <path d="M12 1V4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">New Booking</h3>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="bdp-close-btn" onClick={() => this.setMinimised(true)} title="Minimise">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 10H12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
            <button className="bdp-close-btn" onClick={this.props.onClose} title="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="bdp-body">
          {/* Staff */}
          {staffList && staffList.length > 0 && (
            <div className="bdp-edit-field">
              <label className="bdp-edit-label">Staff Member</label>
              <StaffPicker
                label="Staff Member"
                staffList={staffList}
                value={staffId}
                onChange={(id) => { this.setState({ staffId: id }, () => this.loadAvailableServices()); }}
              />
            </div>
          )}

          {/* Date */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">Date</label>
            <div
              className="bdp-edit-input"
              ref={el => { this._dateAnchor = el; }}
              onClick={() => this.setState({ datePickerOpen: true })}
              style={!bookingDate ? { color: '#999' } : undefined}
            >
              {bookingDate ? moment(bookingDate).format('ddd D MMM YYYY') : 'Select date'}
            </div>
            <DiaryDatePicker
              open={datePickerOpen}
              selected={bookingDate || new Date()}
              anchorEl={this._dateAnchor}
              onChange={(date) => {
                this.setState({ bookingDate: moment(date).format('YYYY-MM-DD'), datePickerOpen: false });
              }}
              onClose={() => this.setState({ datePickerOpen: false })}
            />
          </div>

          {/* Start Time */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">Start Time</label>
            <ShiftTimePicker
              label="Start Time"
              selected={startMinutes != null ? (() => { const d = new Date(); d.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0); return d; })() : null}
              placeholder="Select time"
              onChange={(date) => this.setState({ startMinutes: date.getHours() * 60 + date.getMinutes() })}
            />
          </div>

          {/* Client */}
          <div className="bdp-section">
            <h4 className="bdp-section-title">Client</h4>
            {selectedClient ? (
              <button
                type="button"
                className="nbp-selected-client bdp-client-tappable"
                onClick={this.handleClearClient}
              >
                <div className="nbp-selected-client-info">
                  <span className="nbp-selected-client-name">{selectedClient.name}</span>
                  {selectedClient.email && !selectedClient.email.includes('@beuapp.com') && (
                    <span className="nbp-selected-client-sub">{selectedClient.email}</span>
                  )}
                  {selectedClient.phoneNumber && (
                    <span className="nbp-selected-client-sub">{selectedClient.phoneNumber}</span>
                  )}
                </div>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4L10 8L6 12" stroke="#c7c7cc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ) : (
              <div className="nbp-client-actions">
                <button type="button" className="nbp-select-client-btn" onClick={() => { this.setState({ screen: 'clientPicker', clientSearchQuery: '' }); this.handleClientSearch(''); }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2"/>
                    <path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  Search clients
                </button>
                <button type="button" className="nbp-walkin-btn" onClick={this.handleWalkIn}>Walk-in</button>
              </div>
            )}
            {this.renderBelongsTo()}
          </div>

          {/* Services */}
          <div className="bdp-section">
            <div className="bdp-edit-section-header">
              <h4 className="bdp-section-title">Services</h4>
            </div>
            {services.length === 0 && (
              <p className="bdp-edit-empty">No services added yet</p>
            )}
            {/* Setup buffer pill — sits above the first service. Editing
                it overrides the service-default setupTime for this booking. */}
            {services.length > 0 && (
              (services[0].setupTime || 0) > 0 ? (
                <div className="bdp-processing-spacer">
                  <span className="bdp-processing-spacer-line" />
                  <div className="bdp-processing-spacer-content">
                    <span className="bdp-processing-spacer-label">Setup</span>
                    <div className="bdp-processing-input-wrap">
                      <DurationPicker
                        label="Setup Time"
                        value={services[0].setupTime}
                        onChange={(mins) => this.handleSetupTimeChange(mins)}
                      />
                      <button type="button" className="bdp-processing-remove"
                        onClick={() => this.handleSetupTimeChange(0)} title="Remove setup time">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <span className="bdp-processing-spacer-line" />
                </div>
              ) : (
                <div className="bdp-processing-spacer bdp-processing-spacer--add">
                  <button type="button" className="bdp-add-processing-pill"
                    onClick={() => this.handleSetupTimeChange(15)}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M5 1.5V8.5M1.5 5H8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                    Setup time
                  </button>
                </div>
              )
            )}
            <div className="bdp-service-blocks">
              {services.map((s, i) => {
                // First service starts after the setup buffer; subsequent
                // services follow the previous service + its processing time.
                let serviceStartMin = startMinutes + (services[0].setupTime || 0);
                for (let j = 0; j < i; j++) {
                  serviceStartMin += (services[j].serviceTime || 0) + (services[j].processingTime || 0)
                    + (services[j].finishingTime || 0);
                }
                const svcColour = s.service?.diaryColour || s.diaryColour || '#c7c7cc';
                // A service with finishing time keeps its processing (apply →
                // processing → finish), even as the last/only service.
                const hasFinishing = (s.finishingTime || 0) > 0;
                return (
                  <React.Fragment key={i}>
                    <div className="bdp-svc-card">
                      {/* Service name - tap to change */}
                      <button type="button" className="bdp-svc-card-header" onClick={() => this.handleOpenServicePicker(i)}>
                        <span className="bdp-svc-card-color" style={{ backgroundColor: svcColour }} />
                        <span className="bdp-svc-card-name">{s.service?.serviceName || s.serviceName}</span>
                        <svg className="bdp-svc-card-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      {/* Timing + Duration */}
                      <div className="bdp-svc-card-fields">
                        <div className="bdp-svc-card-field">
                          <label className="bdp-svc-card-label">Timing</label>
                          <div className="bdp-svc-card-value">{formatTime(serviceStartMin)} - {formatTime(serviceStartMin + (s.serviceTime || 0))}</div>
                        </div>
                        <div className="bdp-svc-card-field">
                          <label className="bdp-svc-card-label">Duration</label>
                          <DurationPicker
                            label="Duration"
                            value={s.serviceTime || 30}
                            onChange={(mins) => this.handleServiceDurationChange(i, mins)}
                          />
                        </div>
                      </div>
                      {/* Price */}
                      <div className="bdp-svc-card-fields">
                        <div className="bdp-svc-card-field bdp-svc-card-field--full">
                          <label className="bdp-svc-card-label">Price</label>
                          <div className="bdp-svc-card-value">{currency}{parseFloat(s.servicePrice || 0).toFixed(2)}</div>
                        </div>
                      </div>
                      {/* Actions (only if multiple services) */}
                      {services.length > 1 && (
                        <div className="bdp-svc-card-actions">
                          <button type="button" className="bdp-svc-card-btn" disabled={i === 0}
                            onClick={() => this.handleMoveService(i, -1)}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M2 7.5L6 3.5L10 7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Move up
                          </button>
                          <button type="button" className="bdp-svc-card-btn" disabled={i === services.length - 1}
                            onClick={() => this.handleMoveService(i, 1)}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M2 4.5L6 8.5L10 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Move down
                          </button>
                          <button type="button" className="bdp-svc-card-btn bdp-svc-card-btn--remove"
                            onClick={() => this.handleRemoveService(i)}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                            </svg>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Processing time spacer between services, or before finishing */}
                    {i < services.length - 1 || hasFinishing ? (
                      s.processingTime > 0 ? (
                        <div className="bdp-processing-spacer">
                          <span className="bdp-processing-spacer-line" />
                          <div className="bdp-processing-spacer-content">
                            <span className="bdp-processing-spacer-label">Processing</span>
                            <div className="bdp-processing-input-wrap">
                              <DurationPicker
                                label="Processing Time"
                                value={s.processingTime}
                                onChange={(mins) => this.handleProcessingTimeChange(i, mins)}
                              />
                              <button type="button" className="bdp-processing-remove"
                                onClick={() => this.handleProcessingTimeChange(i, 0)} title="Remove processing time">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                          <span className="bdp-processing-spacer-line" />
                        </div>
                      ) : (
                        <div className="bdp-processing-spacer bdp-processing-spacer--add">
                          <button type="button" className="bdp-add-processing-pill"
                            onClick={() => this.handleProcessingTimeChange(i, 15)}>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path d="M5 1.5V8.5M1.5 5H8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            </svg>
                            Processing time
                          </button>
                        </div>
                      )
                    ) : null}
                    {hasFinishing && (
                      <div className="bdp-processing-spacer">
                        <span className="bdp-processing-spacer-line" />
                        <div className="bdp-processing-spacer-content">
                          <span className="bdp-processing-spacer-label">Finishing</span>
                          <div className="bdp-processing-input-wrap">
                            <DurationPicker
                              label="Finishing Time"
                              value={s.finishingTime}
                              onChange={(mins) => this.handleFinishingTimeChange(i, mins)}
                            />
                            <button type="button" className="bdp-processing-remove"
                              onClick={() => this.handleFinishingTimeChange(i, 0)} title="Remove finishing time">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <span className="bdp-processing-spacer-line" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {/* Cleanup buffer pill — sits below the last service, above
                Add Service. Same pattern as setup. */}
            {services.length > 0 && (
              (services[services.length - 1].cleanupTime || 0) > 0 ? (
                <div className="bdp-processing-spacer">
                  <span className="bdp-processing-spacer-line" />
                  <div className="bdp-processing-spacer-content">
                    <span className="bdp-processing-spacer-label">Cleanup</span>
                    <div className="bdp-processing-input-wrap">
                      <DurationPicker
                        label="Cleanup Time"
                        value={services[services.length - 1].cleanupTime}
                        onChange={(mins) => this.handleCleanupTimeChange(mins)}
                      />
                      <button type="button" className="bdp-processing-remove"
                        onClick={() => this.handleCleanupTimeChange(0)} title="Remove cleanup time">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <span className="bdp-processing-spacer-line" />
                </div>
              ) : (
                <div className="bdp-processing-spacer bdp-processing-spacer--add">
                  <button type="button" className="bdp-add-processing-pill"
                    onClick={() => this.handleCleanupTimeChange(15)}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M5 1.5V8.5M1.5 5H8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                    Cleanup time
                  </button>
                </div>
              )
            )}
            {/* Add service */}
            <button type="button" className="bdp-edit-add-btn bdp-edit-add-btn--below" onClick={() => this.handleOpenServicePicker(null)}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Add Service
            </button>
            {/* Finish time + total */}
            {services.length > 0 && (
              <div className="bdp-edit-summary-block">
                <div className="bdp-edit-finish-summary">
                  <span className="bdp-finish-label">Finishes at</span>
                  <span className="bdp-finish-time">{formatTime(endMinutes)}</span>
                  <span className="bdp-finish-duration">{(() => { const h = Math.floor(totalDuration / 60); const m = totalDuration % 60; if (h === 0) return `${m}m total`; if (m === 0) return `${h}h total`; return `${h}h ${m}m total`; })()}</span>
                </div>
                <div className="bdp-total-row">
                  <span>Total</span>
                  <span className="bdp-total-amount">{currency}{totalAmount.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Repeat (not for Bailey's practice booking) */}
          {!this.practice && (
            <RepeatControl
              value={this.state.repeat}
              firstDate={bookingDate}
              onChange={(repeat) => this.setState({ repeat, clashCheck: null, clashChoices: {}, clashTimes: {} })}
            />
          )}

          {/* Notes */}
          <div className="bdp-section">
            <h4 className="bdp-section-title">Booking Note</h4>
            <textarea
              className="bdp-note-textarea"
              rows={2}
              value={bookingNote}
              onChange={(e) => this.setState({ bookingNote: e.target.value })}
              placeholder="Add a note for this booking..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bdp-footer">
          <button className="bdp-btn bdp-btn--primary" style={{ flex: 1 }} onClick={() => this.handleSave(false)} disabled={saving || services.length === 0 || !bookingDate || startMinutes == null}>
            {saving ? 'Saving...' : ruleFor(this.state.repeat.freq) ? bookLabel(this.state.repeat, bookingDate) : 'Save Booking'}
          </button>
        </div>
      </>
    );
  }

  render() {
    let content;
    if (this.state.screen === 'servicePicker') {
      content = <div className="bdp-screen-slide-in" key="servicePicker">{this.renderServicePicker()}</div>;
    } else if (this.state.screen === 'clientPicker') {
      content = <div className="bdp-screen-slide-in" key="clientPicker">{this.renderClientPicker()}</div>;
    } else if (this.state.screen === 'dateCheck' && this.state.clashCheck) {
      content = <div className="bdp-screen-slide-in" key="dateCheck">{this.renderDateCheck()}</div>;
    } else {
      content = this.renderForm();
    }

    return (
      <>
        {/* Minimised pill — draggable on every viewport. Click vs drag is
            decided in handlePillPointerUp based on whether the pointer
            moved beyond PILL_DRAG_THRESHOLD. Mobile uses an icon-only
            compact variant (see CSS). */}
        {this.state.minimised && (
          <div
            ref={this._pillRef}
            className={`nbp-minimised${this.state.pillPos ? ' nbp-minimised--positioned' : ''}`}
            onPointerDown={this.handlePillPointerDown}
            style={this.state.pillPos
              ? { left: this.state.pillPos.x, top: this.state.pillPos.y, right: 'auto', bottom: 'auto' }
              : undefined}
          >
            <div className="nbp-minimised-icon">
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <rect x="2" y="3" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M2 7H16" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M6 1V4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                <path d="M12 1V4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="nbp-minimised-label">
              {this.state.screen === 'dateCheck' ? 'Check the dates' : 'New Booking'}{this.state.selectedClient?.name ? ` for ${this.state.selectedClient.name}` : ''}
            </span>
            <svg className="nbp-minimised-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <polyline points="4,10 8,6 12,10" />
            </svg>
          </div>
        )}

        {/* Panel - hidden when minimised, not unmounted */}
        {!this.state.minimised && (
          <SlidePanel
            ref={this.slidePanelRef}
            onClose={() => {
              const hasClient = this.state.selectedClient && this.state.selectedClient.name;
              const hasServices = this.state.services && this.state.services.length > 0;
              if (hasClient || hasServices) {
                this.setMinimised(true);
              } else {
                this.props.onClose();
              }
            }}
            className="bdp-panel nbp-panel"
            width={420}
          >
            {content}
          </SlidePanel>
        )}
        {this.state.showAddClient && (
          <AddClientPanel
            initial={this.state.selectedClient ? null : this.props.newClient}
            onClose={() => this.setState({ showAddClient: false })}
            onClientAdded={(newClient) => {
              // Auto-select the new client and go back to form
              if (newClient && newClient.name) {
                this.setState({
                  showAddClient: false,
                  screen: 'form',
                  selectedClient: {
                    _id: newClient._id || newClient.id || '',
                    name: newClient.name,
                    email: newClient.email || '',
                    phoneNumber: newClient.phoneNumber || '',
                  },
                });
              } else {
                // Fallback: close and refresh list
                this.setState({ showAddClient: false });
                this.handleClientSearch('');
              }
            }}
            history={this.props.history}
          />
        )}
      </>
    );
  }
}

export default NewBookingPanel;
